// tslint:disable: max-classes-per-file
import { BigNumber } from '@0x/utils'
import { sleep } from 'app-lib/asyncUtils'
import stakingUtils from 'app-lib/stakingUtils'
import * as mobx from 'mobx'
import RootStore from '../RootStore'

type userBalanceProp = 'pendingWalletBalance' | 'pendingStakedBalance' | 'error'

export interface IStakingTokenBalances {
  ooki: BigNumber
  iooki: BigNumber
  vbzrx: BigNumber
  crv3: BigNumber
  slp_ooki_eth: BigNumber
  sushi: BigNumber
}

export class TokenBalances implements IStakingTokenBalances {
  [name: string]: any
  public ooki = new BigNumber(0)
  public iooki = new BigNumber(0)
  public vbzrx = new BigNumber(0)
  public crv3 = new BigNumber(0)
  public slp_ooki_eth = new BigNumber(0)
  public sushi = new BigNumber(0)

  get isEmpty() {
    // Note: we dont count 3CRV or SUSHI because we dont use them for staking, it's just a reward
    return (
      this.ooki.isZero() && this.iooki.isZero() && this.slp_ooki_eth.isZero() && this.vbzrx.isZero()
    )
  }

  /**
   * An helper that tells if user wallet balances are enough to stake / unstake
   */
  get isWorthEnough() {
    return (
      this.ooki.gte(0.01) ||
      this.iooki.gte(0.01) ||
      this.slp_ooki_eth.gte(0.01) ||
      this.vbzrx.gte(0.01)
    )
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: keyof IStakingTokenBalances, value: BigNumber) {
    this[prop] = value
  }

  public add(amounts: Partial<IStakingTokenBalances>) {
    if (amounts.ooki) {
      this.ooki = this.ooki.plus(amounts.ooki)
    }
    if (amounts.vbzrx) {
      this.vbzrx = this.vbzrx.plus(amounts.vbzrx)
    }
    if (amounts.iooki) {
      this.iooki = this.iooki.plus(amounts.iooki)
    }
    if (amounts.crv3) {
      this.crv3 = this.crv3.plus(amounts.crv3)
    }
    if (amounts.slp_ooki_eth) {
      this.slp_ooki_eth = this.slp_ooki_eth.plus(amounts.slp_ooki_eth)
    }
    if (amounts.sushi) {
      this.sushi = this.sushi.plus(amounts.sushi)
    }
  }

  public substract(amounts: Partial<IStakingTokenBalances>) {
    if (amounts.ooki) {
      this.ooki = this.ooki.minus(amounts.ooki)
    }
    if (amounts.vbzrx) {
      this.vbzrx = this.vbzrx.minus(amounts.vbzrx)
    }
    if (amounts.iooki) {
      this.iooki = this.iooki.minus(amounts.iooki)
    }
    if (amounts.crv3) {
      this.crv3 = this.crv3.minus(amounts.crv3)
    }
    if (amounts.slp_ooki_eth) {
      this.slp_ooki_eth = this.slp_ooki_eth.minus(amounts.slp_ooki_eth)
    }
    if (amounts.sushi) {
      this.sushi = this.sushi.minus(amounts.sushi)
    }
  }

  public clearBalances() {
    this.ooki = new BigNumber(0)
    this.iooki = new BigNumber(0)
    this.vbzrx = new BigNumber(0)
    this.crv3 = new BigNumber(0)
    this.slp_ooki_eth = new BigNumber(0)
    this.sushi = new BigNumber(0)
  }

  public getCopy() {
    const balances = new TokenBalances()
    balances.set('ooki', new BigNumber(this.ooki))
    balances.set('vbzrx', new BigNumber(this.vbzrx))
    balances.set('iooki', new BigNumber(this.iooki))
    balances.set('crv3', new BigNumber(this.crv3))
    balances.set('slp_ooki_eth', new BigNumber(this.slp_ooki_eth))
    balances.set('sushi', new BigNumber(this.sushi))
    return balances
  }

  constructor() {
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}

export default class UserBalances {
  public rootStore: RootStore
  public wallet = new TokenBalances()
  public staked = new TokenBalances()
  public pendingWalletBalance = false
  public pendingStakedBalance = false
  public error: Error | null = null
  public loaded = false

  get total() {
    return {
      ooki: this.wallet.ooki.plus(this.staked.ooki),
      iooki: this.wallet.iooki.plus(this.staked.iooki),
      vbzrx: this.wallet.vbzrx.plus(this.staked.vbzrx),
      slp_ooki_eth: this.wallet.slp_ooki_eth.plus(this.staked.slp_ooki_eth),
    }
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: userBalanceProp, value: any) {
    this[prop] = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  public async refreshWalletbalances() {
    const fp = this.rootStore.fulcrumProvider
    this.set('pendingWalletBalance', true)
    const walletAmounts = await stakingUtils.getStakeableBalances(fp)
    this.set('pendingWalletBalance', false)
    mobx.runInAction(() => {
      this.wallet.ooki = walletAmounts.ooki.div(10 ** 18)
      this.wallet.vbzrx = walletAmounts.vbzrx.div(10 ** 18)
      this.wallet.iooki = walletAmounts.iooki.div(10 ** 18)
      this.wallet.slp_ooki_eth = walletAmounts.slp_ooki_eth.div(10 ** 18)
    })
  }

  public async refreshStakedBalances() {
    const fp = this.rootStore.fulcrumProvider
    this.set('pendingStakedBalance', true)
    const stakedAmounts = await stakingUtils.getStakedBalances(fp)
    this.set('pendingStakedBalance', false)
    mobx.runInAction(() => {
      this.staked.ooki = stakedAmounts.ooki.div(10 ** 18)
      this.staked.vbzrx = stakedAmounts.vbzrx.div(10 ** 18)
      this.staked.iooki = stakedAmounts.iooki.div(10 ** 18)
      this.staked.slp_ooki_eth = stakedAmounts.slp_ooki_eth.div(10 ** 18)
    })
  }

  /**
   * Get and store the staking specific tokens the user has in wallet and already staked.
   */
  public async getUserBalances() {
    try {
      await this.refreshWalletbalances()
      await sleep(1000) // Avoids breaking animation due to main thread blocked
      await this.refreshStakedBalances()
      return { wallet: this.wallet, staked: this.staked }
    } catch (err) {
      err.title = 'Failed to get balances'
      this.set('error', err)
      console.error(err)
    } finally {
      this.assign({ loaded: true, pendingStakedBalance: false, pendingWalletBalance: false })
    }
  }

  public clearBalances() {
    this.wallet.clearBalances()
    this.staked.clearBalances()
    this.loaded = false
  }

  public getCopy() {
    return {
      wallet: this.wallet.getCopy(),
      staked: this.staked.getCopy(),
    }
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}
